import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService, getCurrentUser, checkAuthenticationStatus } from '../services/authService';

// Create Authentication Context
const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authService.initialize();
        
        // Check authentication status via API call (cookie-based JWT)
        const authResult = await checkAuthenticationStatus();
        setIsAuthenticated(authResult.isAuthenticated);
        
        if (authResult.isAuthenticated && authResult.user) {
          setUser(authResult.user);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Listen for authentication changes (e.g., after SAML login)
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Use API-based authentication check instead of just sessionStorage
        const authResult = await checkAuthenticationStatus();
        
        if (authResult.isAuthenticated !== isAuthenticated) {
          setIsAuthenticated(authResult.isAuthenticated);
          
          if (authResult.isAuthenticated && authResult.user) {
            setUser(authResult.user);
          } else {
            setUser(null);
          }
        }
      } catch (error) {
        // On error, consider user not authenticated
        if (isAuthenticated) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    };

    // Listen for storage changes (when authentication data is added/removed)
    window.addEventListener('storage', checkAuthStatus);
    
    // Also check periodically for authentication state changes (every 5 seconds)
    // Reduced frequency since we're making API calls
    const interval = setInterval(checkAuthStatus, 5000);

    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      clearInterval(interval);
    };
  }, [user, isAuthenticated]);

  // Login function
  const login = async () => {
    try {
      await authService.login();
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  // Refresh authentication state manually
  const refreshAuth = async () => {
    try {
      // Use API-based authentication check
      const authResult = await checkAuthenticationStatus();
      setIsAuthenticated(authResult.isAuthenticated);
      
      if (authResult.isAuthenticated && authResult.user) {
        setUser(authResult.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Authentication context value
  const value = {
    user,
    login,
    logout,
    refreshAuth,
    isAuthenticated,
    loading
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '64px', height: '64px' }} />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;